import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useExternalServiceProvider } from '@/hooks/useExternalServiceProvider';
import { isApiError } from '@/utils/errors';

function isStaffDashboardPath(pathname: string): boolean {
  return pathname === '/tyopoyta';
}

/**
 * When a server query fails with 403 (e.g. after switching company on a detail page),
 * send staff users to the main dashboard so they do not stay on a broken screen.
 *
 * ESP portal users must not be sent to `/tyopoyta` — that path is outside the ESP
 * route tree and the ESP catch-all bounces them to `/`, remounting the shell and
 * cancelling in-flight navigations / modals.
 */
export function ForbiddenQueryRedirectListener() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isExternalServiceProvider } = useExternalServiceProvider();

  useEffect(() => {
    if (isExternalServiceProvider) return;

    const cache = queryClient.getQueryCache();
    return cache.subscribe((event) => {
      if (event.type !== 'updated') return;
      const query = event.query;
      if (query.state.status !== 'error' || query.state.fetchStatus !== 'idle') return;
      const err = query.state.error;
      if (!isApiError(err) || err.status !== 403) return;

      const pathname = window.location.pathname;
      // Detail pages render their own unavailable/not-found state. A failed
      // related query must not replace the requested URL with the dashboard.
      if (isStaffDashboardPath(pathname) || /\/\d+\/?$/.test(pathname)) return;

      navigate('/tyopoyta', { replace: true });
    });
  }, [isExternalServiceProvider, navigate, queryClient]);

  return null;
}
